import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { sv } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PreviewIcon from '@mui/icons-material/Preview';
import { IPopupItem, IAsset, TitlePosition, BadgeStyle, BadgePosition, BodyImagePosition, CtaStyle } from '../types/damTypes';
import { API_BASE_URL } from '../config';
import axiosInstance from '../services/axiosInstance';

interface PopupItemEditorProps {
  open: boolean;
  item: IPopupItem | null;
  onClose: () => void;
  onSave: () => void;
}

// Color presets for badge
const BADGE_COLORS = [
  { color: '#8B1538', name: 'Vinröd' },
  { color: '#1976D2', name: 'Blå' },
  { color: '#388E3C', name: 'Grön' },
  { color: '#F57C00', name: 'Orange' },
  { color: '#7B1FA2', name: 'Lila' },
  { color: '#455A64', name: 'Grå' },
];

// Title position options
const TITLE_POSITIONS: { value: TitlePosition; label: string }[] = [
  { value: 'top-left', label: 'Uppe vänster' },
  { value: 'top-center', label: 'Uppe mitten' },
  { value: 'top-right', label: 'Uppe höger' },
  { value: 'middle-left', label: 'Mitten vänster' },
  { value: 'middle-center', label: 'Mitten' },
  { value: 'middle-right', label: 'Mitten höger' },
  { value: 'bottom-left', label: 'Nere vänster' },
  { value: 'bottom-center', label: 'Nere mitten' },
  { value: 'bottom-right', label: 'Nere höger' },
];

// Badge style options
const BADGE_STYLES: { value: BadgeStyle; label: string; description: string }[] = [
  { value: 'ribbon-horizontal', label: 'Vimpel höger', description: 'Horisontell vimpel med spets åt höger' },
  { value: 'ribbon-vertical', label: 'Vimpel nedåt', description: 'Vertikal vimpel med spets nedåt' },
  { value: 'corner-fold', label: 'Hörnflik', description: 'Diagonal flik i hörnet' },
  { value: 'circle', label: 'Cirkel', description: 'Rund badge' },
  { value: 'tag', label: 'Tagg', description: 'Enkel rektangulär tagg' },
];

const PopupItemEditor: React.FC<PopupItemEditorProps> = ({ open, item, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    titlePosition: 'bottom-left' as TitlePosition,
    titleSize: 2.5,
    bodyText: '',
    bodyBackgroundColor: '#072f53',
    bodyTextColor: '#FFFFFF',
    bodyTitle: '',
    bodyTitleSize: 1.6,
    bodyImageUrl: '',
    bodyImagePosition: 'none' as BodyImagePosition,
    imageUrl: '',
    imageAssetId: '',
    badgeText: 'NYHET!',
    badgeColor: '#8B1538',
    badgeTextColor: '#FFFFFF',
    badgeStyle: 'ribbon-vertical' as BadgeStyle,
    badgeScale: 1.0,
    badgePosition: 'top-left' as BadgePosition,
    badgeOffsetY: 0,
    ctaText: '',
    ctaLink: '',
    ctaButtonColor: '#8B1538',
    ctaStyle: 'text-link' as CtaStyle,
    startDate: null as Date | null,
    endDate: null as Date | null,
    isActive: true
  });
  
  const [imageTab, setImageTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // DAM image selection
  const [damAssets, setDamAssets] = useState<IAsset[]>([]);
  const [loadingDam, setLoadingDam] = useState(false);
  const [damSearch, setDamSearch] = useState('');

  // Initialize form when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || '',
        titlePosition: item.titlePosition || 'bottom-left',
        titleSize: item.titleSize || 2.5,
        bodyText: item.bodyText || '',
        bodyBackgroundColor: item.bodyBackgroundColor || '#072f53',
        bodyTextColor: item.bodyTextColor || '#FFFFFF',
        bodyTitle: item.bodyTitle || '',
        bodyTitleSize: item.bodyTitleSize || 1.6,
        bodyImageUrl: item.bodyImageUrl || '',
        bodyImagePosition: item.bodyImagePosition || 'none',
        imageUrl: item.imageUrl || '',
        imageAssetId: item.imageAssetId || '',
        badgeText: item.badgeText || 'NYHET!',
        badgeColor: item.badgeColor || '#8B1538',
        badgeTextColor: item.badgeTextColor || '#FFFFFF',
        badgeStyle: item.badgeStyle || 'ribbon-vertical',
        badgeScale: item.badgeScale || 1.0,
        badgePosition: item.badgePosition || 'top-left',
        badgeOffsetY: item.badgeOffsetY || 0,
        ctaText: item.ctaText || '',
        ctaLink: item.ctaLink || '',
        ctaButtonColor: item.ctaButtonColor || '#8B1538',
        ctaStyle: item.ctaStyle || 'text-link',
        startDate: item.startDate ? new Date(item.startDate) : null,
        endDate: item.endDate ? new Date(item.endDate) : null,
        isActive: item.isActive !== false
      });
    } else {
      // Reset form for new item
      setFormData({
        title: '',
        titlePosition: 'bottom-left',
        titleSize: 2.5,
        bodyText: '',
        bodyBackgroundColor: '#072f53',
        bodyTextColor: '#FFFFFF',
        bodyTitle: '',
        bodyTitleSize: 1.6,
        bodyImageUrl: '',
        bodyImagePosition: 'none',
        imageUrl: '',
        imageAssetId: '',
        badgeText: 'NYHET!',
        badgeColor: '#8B1538',
        badgeTextColor: '#FFFFFF',
        badgeStyle: 'ribbon-vertical',
        badgeScale: 1.0,
        badgePosition: 'top-left',
        badgeOffsetY: 0,
        ctaText: '',
        ctaLink: '',
        ctaButtonColor: '#8B1538',
        ctaStyle: 'text-link',
        startDate: null,
        endDate: null,
        isActive: true
      });
    }
    setError(null);
  }, [item, open]);

  // Fetch DAM images
  const fetchDamImages = async () => {
    setLoadingDam(true);
    try {
      const params = new URLSearchParams();
      params.append('fileType', 'image');
      params.append('limit', '50');
      if (damSearch) {
        params.append('search', damSearch);
      }
      
      const res = await axiosInstance.get(`/api/assets?${params.toString()}`);
      setDamAssets(res.data.assets || []);
    } catch (err) {
      console.error('Error fetching DAM images:', err);
    } finally {
      setLoadingDam(false);
    }
  };

  useEffect(() => {
    if (imageTab === 1 && open) {
      fetchDamImages();
    }
  }, [imageTab, open]);

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const res = await axiosInstance.post('/api/popup/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({
        ...prev,
        imageUrl: res.data.imageUrl,
        imageAssetId: ''
      }));
    } catch (err: any) {
      setError('Kunde inte ladda upp bilden');
    } finally {
      setUploading(false);
    }
  };

  // Select DAM image
  const handleSelectDamImage = (asset: IAsset) => {
    // Use the thumbnail or preview path
    const imageUrl = asset.previewPaths?.medium || asset.previewPaths?.thumbnail || asset.filePath;
    setFormData(prev => ({
      ...prev,
      imageUrl: imageUrl,
      imageAssetId: asset._id
    }));
  };

  // Save
  const handleSave = async () => {
    if (!formData.imageUrl) {
      setError('Bild är obligatorisk');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        startDate: formData.startDate?.toISOString(),
        endDate: formData.endDate?.toISOString()
      };

      if (item) {
        await axiosInstance.put(`/api/popup/items/${item._id}`, payload);
      } else {
        await axiosInstance.post('/api/popup/items', payload);
      }

      onSave();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const getImageSrc = (url: string) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {item ? 'Redigera nyhet' : 'Lägg till nyhet'}
          </Typography>
          <Box>
            <IconButton onClick={() => setShowPreview(!showPreview)} color={showPreview ? 'primary' : 'default'}>
              <PreviewIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {/* Left column - Form */}
            <Box sx={{ flex: showPreview ? '1 1 45%' : '1 1 100%', minWidth: 300 }}>
              {/* Title on image */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Rubrik på bilden
                </Typography>
                <TextField
                  label="Rubrik (lämna tom för ingen)"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                  placeholder="Sriracha, nyhet vecka 11"
                />
              
                {/* Title Position - only show if title has text */}
                {formData.title && (
                  <>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Rubrikens placering</InputLabel>
                      <Select
                        value={formData.titlePosition}
                        label="Rubrikens placering"
                        onChange={(e) => setFormData({ ...formData, titlePosition: e.target.value as TitlePosition })}
                      >
                        {TITLE_POSITIONS.map(pos => (
                          <MenuItem key={pos.value} value={pos.value}>{pos.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Title Size */}
                    <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                      Rubrikstorlek: {formData.titleSize.toFixed(1)} rem
                    </Typography>
                    <Slider
                      value={formData.titleSize}
                      onChange={(e, v) => setFormData({ ...formData, titleSize: v as number })}
                      min={1.0}
                      max={4.0}
                      step={0.25}
                      marks={[
                        { value: 1.5, label: 'Liten' },
                        { value: 2.5, label: 'Normal' },
                        { value: 3.5, label: 'Stor' },
                      ]}
                      sx={{ maxWidth: 300 }}
                    />
                  </>
                )}
              </Paper>

              {/* Image selection */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Huvudbild *
                </Typography>
                <Tabs value={imageTab} onChange={(e, v) => setImageTab(v)} sx={{ mb: 2 }}>
                  <Tab icon={<CloudUploadIcon />} label="Ladda upp" />
                  <Tab icon={<PhotoLibraryIcon />} label="Välj från DAM" />
                </Tabs>

                {imageTab === 0 && (
                  <Box>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      id="popup-image-upload"
                    />
                    <label htmlFor="popup-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                        disabled={uploading}
                      >
                        {uploading ? 'Laddar upp...' : 'Välj fil'}
                      </Button>
                    </label>
                  </Box>
                )}

                {imageTab === 1 && (
                  <Box>
                    <TextField
                      placeholder="Sök bilder..."
                      size="small"
                      value={damSearch}
                      onChange={(e) => setDamSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && fetchDamImages()}
                      sx={{ mb: 2, width: '100%' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button size="small" onClick={fetchDamImages}>Sök</Button>
                          </InputAdornment>
                        )
                      }}
                    />
                    
                    {loadingDam ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 200, overflow: 'auto' }}>
                        {damAssets.map(asset => (
                          <Box
                            key={asset._id}
                            onClick={() => handleSelectDamImage(asset)}
                            sx={{
                              width: 80,
                              height: 60,
                              borderRadius: 1,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              border: formData.imageAssetId === asset._id ? '3px solid' : '1px solid',
                              borderColor: formData.imageAssetId === asset._id ? 'primary.main' : 'divider',
                              '&:hover': { borderColor: 'primary.main' }
                            }}
                          >
                            <img
                              src={getImageSrc(asset.previewPaths?.thumbnail || asset.filePath)}
                              alt={asset.title}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Preview current image */}
                {formData.imageUrl && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">Vald bild:</Typography>
                    <Box
                      sx={{
                        mt: 1,
                        width: '100%',
                        maxWidth: 300,
                        aspectRatio: '16/10',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <img
                        src={getImageSrc(formData.imageUrl)}
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  </Box>
                )}
              </Paper>

              {/* Badge */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Badge
                </Typography>
                <TextField
                  label="Badge-text"
                  value={formData.badgeText}
                  onChange={(e) => setFormData({ ...formData, badgeText: e.target.value })}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                  placeholder="NYHET!"
                />

                {/* Badge Style */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Badge-stil</InputLabel>
                  <Select
                    value={formData.badgeStyle}
                    label="Badge-stil"
                    onChange={(e) => setFormData({ ...formData, badgeStyle: e.target.value as BadgeStyle })}
                  >
                    {BADGE_STYLES.map(style => (
                      <MenuItem key={style.value} value={style.value}>
                        {style.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Badge Position */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Position</InputLabel>
                    <Select
                      value={formData.badgePosition}
                      label="Position"
                      onChange={(e) => setFormData({ ...formData, badgePosition: e.target.value as BadgePosition })}
                    >
                      <MenuItem value="top-right">Höger</MenuItem>
                      <MenuItem value="top-left">Vänster</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Badge Scale */}
                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                  Storlek: {Math.round(formData.badgeScale * 100)}%
                </Typography>
                <Slider
                  value={formData.badgeScale}
                  onChange={(e, v) => setFormData({ ...formData, badgeScale: v as number })}
                  min={0.5}
                  max={2.0}
                  step={0.1}
                  marks={[
                    { value: 0.5, label: '50%' },
                    { value: 1, label: '100%' },
                    { value: 1.5, label: '150%' },
                    { value: 2, label: '200%' },
                  ]}
                  sx={{ mb: 2, maxWidth: 300 }}
                />

                {/* Badge Vertical Offset */}
                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                  Höjdjustering: {formData.badgeOffsetY}px
                </Typography>
                <Slider
                  value={formData.badgeOffsetY}
                  onChange={(e, v) => setFormData({ ...formData, badgeOffsetY: v as number })}
                  min={0}
                  max={80}
                  step={5}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 40, label: '40' },
                    { value: 80, label: '80' },
                  ]}
                  sx={{ mb: 2, maxWidth: 300 }}
                />
                
                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                  Badge-färg:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {BADGE_COLORS.map(({ color, name }) => (
                    <Box
                      key={color}
                      onClick={() => setFormData({ ...formData, badgeColor: color })}
                      title={name}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        bgcolor: color,
                        cursor: 'pointer',
                        border: formData.badgeColor === color ? '3px solid #333' : '1px solid rgba(0,0,0,0.2)',
                        '&:hover': { transform: 'scale(1.1)' },
                        transition: 'transform 0.1s'
                      }}
                    />
                  ))}
                  <TextField
                    type="color"
                    value={formData.badgeColor}
                    onChange={(e) => setFormData({ ...formData, badgeColor: e.target.value })}
                    size="small"
                    sx={{ width: 60 }}
                    inputProps={{ style: { height: 24, padding: 4 } }}
                  />
                </Box>
              </Paper>

              {/* Body Section */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Brödtextområde
                </Typography>
                {/* Body Title */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                  <TextField
                    label="Rubrik i brödtextområdet"
                    value={formData.bodyTitle}
                    onChange={(e) => setFormData({ ...formData, bodyTitle: e.target.value })}
                    size="small"
                    sx={{ flex: 1 }}
                    placeholder="RUBRIK RUBRIK!"
                  />
                  {formData.bodyTitle && (
                    <Box sx={{ minWidth: 150 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Storlek: {formData.bodyTitleSize.toFixed(1)} rem
                      </Typography>
                      <Slider
                        value={formData.bodyTitleSize}
                        onChange={(e, v) => setFormData({ ...formData, bodyTitleSize: v as number })}
                        min={1.0}
                        max={3.0}
                        step={0.1}
                        size="small"
                      />
                    </Box>
                  )}
                </Box>

                {/* Body text */}
                <TextField
                  label="Brödtext (stöder HTML)"
                  value={formData.bodyText}
                  onChange={(e) => setFormData({ ...formData, bodyText: e.target.value })}
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={15}
                  sx={{ 
                    mb: 1,
                    '& .MuiInputBase-inputMultiline': {
                      resize: 'vertical',
                      overflow: 'auto !important'
                    }
                  }}
                  placeholder="Beskriv nyheten mer utförligt..."
                  helperText="Du kan använda HTML-taggar som <b>, <i>, <br>, <a href='...'>. Dra i hörnet för att förstora."
                />

                {/* Body Image */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                    Bild i brödtextområdet (valfritt):
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Bildplacering</InputLabel>
                      <Select
                        value={formData.bodyImagePosition}
                        label="Bildplacering"
                        onChange={(e) => setFormData({ ...formData, bodyImagePosition: e.target.value as BodyImagePosition })}
                      >
                        <MenuItem value="none">Ingen bild</MenuItem>
                        <MenuItem value="right">Höger</MenuItem>
                        <MenuItem value="left">Vänster</MenuItem>
                      </Select>
                    </FormControl>
                    
                    {formData.bodyImagePosition !== 'none' && (
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              try {
                                const formDataUpload = new FormData();
                                formDataUpload.append('image', file);
                                const res = await axiosInstance.post('/api/popup/upload', formDataUpload, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                setFormData(prev => ({ ...prev, bodyImageUrl: res.data.imageUrl }));
                              } catch (err) {
                                console.error('Error uploading body image:', err);
                              }
                            }}
                            style={{ display: 'none' }}
                            id="body-image-upload"
                          />
                          <label htmlFor="body-image-upload">
                            <Button variant="outlined" component="span" size="small" startIcon={<CloudUploadIcon />}>
                              Ladda upp
                            </Button>
                          </label>
                          <Typography variant="caption" color="text.secondary">eller</Typography>
                          <TextField
                            label="Bild-URL"
                            value={formData.bodyImageUrl}
                            onChange={(e) => setFormData({ ...formData, bodyImageUrl: e.target.value })}
                            size="small"
                            sx={{ flex: 1 }}
                            placeholder="URL från DAM..."
                          />
                        </Box>
                        {formData.bodyImageUrl && (
                          <Box sx={{ mt: 1 }}>
                            <img
                              src={getImageSrc(formData.bodyImageUrl)}
                              alt="Body preview"
                              style={{ maxWidth: 100, maxHeight: 60, objectFit: 'cover', borderRadius: 4 }}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Colors */}
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {/* Body Background Color */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                      Bakgrundsfärg:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {[
                        { color: '#FFFFFF', name: 'Vit' },
                        { color: '#F5F5F5', name: 'Ljusgrå' },
                        { color: '#072f53', name: 'Mörkblå' },
                        { color: '#8B1538', name: 'Vinröd' },
                      ].map(({ color, name }) => (
                        <Box
                          key={color}
                          onClick={() => setFormData({ ...formData, bodyBackgroundColor: color })}
                          title={name}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            bgcolor: color,
                            cursor: 'pointer',
                            border: formData.bodyBackgroundColor === color ? '3px solid #333' : '1px solid rgba(0,0,0,0.2)',
                            '&:hover': { transform: 'scale(1.1)' },
                            transition: 'transform 0.1s'
                          }}
                        />
                      ))}
                      <TextField
                        type="color"
                        value={formData.bodyBackgroundColor}
                        onChange={(e) => setFormData({ ...formData, bodyBackgroundColor: e.target.value })}
                        size="small"
                        sx={{ width: 50 }}
                        inputProps={{ style: { height: 20, padding: 4 } }}
                      />
                    </Box>
                  </Box>

                  {/* Body Text Color */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                      Textfärg:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {[
                        { color: '#333333', name: 'Mörk' },
                        { color: '#666666', name: 'Grå' },
                        { color: '#FFFFFF', name: 'Vit' },
                      ].map(({ color, name }) => (
                        <Box
                          key={color}
                          onClick={() => setFormData({ ...formData, bodyTextColor: color })}
                          title={name}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            bgcolor: color,
                            cursor: 'pointer',
                            border: formData.bodyTextColor === color ? '3px solid #333' : '1px solid rgba(0,0,0,0.2)',
                            '&:hover': { transform: 'scale(1.1)' },
                            transition: 'transform 0.1s'
                          }}
                        />
                      ))}
                      <TextField
                        type="color"
                        value={formData.bodyTextColor}
                        onChange={(e) => setFormData({ ...formData, bodyTextColor: e.target.value })}
                        size="small"
                        sx={{ width: 50 }}
                        inputProps={{ style: { height: 20, padding: 4 } }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Paper>

              {/* CTA */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Call-to-action (lämna tom för ingen länk)
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '0 0 30%', minWidth: 100 }}>
                    <TextField
                      label="Länktext"
                      value={formData.ctaText}
                      onChange={(e) => setFormData({ ...formData, ctaText: e.target.value })}
                      fullWidth
                      size="small"
                      placeholder="LÄS MER OM PRODUKTEN HÄR!"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 auto', minWidth: 150 }}>
                    <TextField
                      label="Länk"
                      value={formData.ctaLink}
                      onChange={(e) => setFormData({ ...formData, ctaLink: e.target.value })}
                      fullWidth
                      size="small"
                      placeholder="/nyheter/sriracha eller https://..."
                    />
                  </Box>
                </Box>
                
                {formData.ctaText && (
                  <>
                    {/* CTA Style */}
                    <FormControl size="small" sx={{ mb: 2, minWidth: 150 }}>
                      <InputLabel>Stil</InputLabel>
                      <Select
                        value={formData.ctaStyle}
                        label="Stil"
                        onChange={(e) => setFormData({ ...formData, ctaStyle: e.target.value as CtaStyle })}
                      >
                        <MenuItem value="text-link">Textlänk</MenuItem>
                        <MenuItem value="button">Knapp</MenuItem>
                      </Select>
                    </FormControl>
                    
                    {/* CTA Color - only for buttons */}
                    {formData.ctaStyle === 'button' && (
                      <>
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                          Knappfärg:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {BADGE_COLORS.map(({ color, name }) => (
                            <Box
                              key={color}
                              onClick={() => setFormData({ ...formData, ctaButtonColor: color })}
                              title={name}
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                bgcolor: color,
                                cursor: 'pointer',
                                border: formData.ctaButtonColor === color ? '3px solid #333' : '1px solid rgba(0,0,0,0.2)',
                                '&:hover': { transform: 'scale(1.1)' },
                                transition: 'transform 0.1s'
                              }}
                            />
                          ))}
                          <TextField
                            type="color"
                            value={formData.ctaButtonColor}
                            onChange={(e) => setFormData({ ...formData, ctaButtonColor: e.target.value })}
                            size="small"
                            sx={{ width: 60 }}
                            inputProps={{ style: { height: 24, padding: 4 } }}
                          />
                        </Box>
                      </>
                    )}
                  </>
                )}
              </Paper>

              {/* Scheduling */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Schemaläggning (valfritt)
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: '1 1 50%' }}>
                  <DatePicker
                    label="Visa från"
                    value={formData.startDate}
                    onChange={(date) => setFormData({ ...formData, startDate: date })}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 50%' }}>
                  <DatePicker
                    label="Visa till"
                    value={formData.endDate}
                    onChange={(date) => setFormData({ ...formData, endDate: date })}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Box>
              </Box>
              </Paper>
            </Box>

            {/* Right column - Preview (sticky) */}
            {showPreview && (
              <Box sx={{ flex: '1 1 45%', minWidth: 300, position: 'sticky', top: 16, alignSelf: 'flex-start' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Förhandsvisning
                </Typography>
                <Paper sx={{ overflow: 'hidden', borderRadius: 2 }}>
                  {/* Badge + Image */}
                  <Box sx={{ position: 'relative' }}>
                    {/* Badge with different styles */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: (formData.badgeStyle === 'corner-fold' ? -5 : 0) + formData.badgeOffsetY,
                        ...(formData.badgePosition === 'top-right' ? { right: formData.badgeStyle === 'corner-fold' ? -5 : 0 } : { left: formData.badgeStyle === 'corner-fold' ? -5 : 0 }),
                        zIndex: 5,
                        transform: `scale(${formData.badgeScale})`,
                        transformOrigin: formData.badgePosition === 'top-right' ? 'top right' : 'top left'
                      }}
                    >
                      {/* Ribbon Horizontal */}
                      {formData.badgeStyle === 'ribbon-horizontal' && (
                        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                          <Box
                            sx={{
                              bgcolor: formData.badgeColor,
                              color: formData.badgeTextColor,
                              px: 2,
                              py: 1,
                              fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                              fontWeight: 600,
                              fontSize: '0.9rem',
                              letterSpacing: '0.05em'
                            }}
                          >
                            {formData.badgeText || 'NYHET!'}
                          </Box>
                          <Box
                            sx={{
                              width: 0,
                              height: 0,
                              borderTop: '16px solid transparent',
                              borderBottom: '16px solid transparent',
                              borderRight: `12px solid ${formData.badgeColor}`,
                              transform: 'rotate(180deg)'
                            }}
                          />
                        </Box>
                      )}

                      {/* Ribbon Vertical */}
                      {formData.badgeStyle === 'ribbon-vertical' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box
                            sx={{
                              bgcolor: formData.badgeColor,
                              color: formData.badgeTextColor,
                              px: 2,
                              pt: 1.5,
                              pb: 2,
                              fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                              fontWeight: 700,
                              fontSize: '0.9rem',
                              letterSpacing: '0.05em',
                              textAlign: 'center',
                              minWidth: 60
                            }}
                          >
                            {formData.badgeText || 'NYHET!'}
                          </Box>
                          <Box
                            sx={{
                              width: 0,
                              height: 0,
                              borderLeft: '30px solid ' + formData.badgeColor,
                              borderRight: '30px solid ' + formData.badgeColor,
                              borderBottom: '14px solid transparent',
                            }}
                          />
                        </Box>
                      )}

                      {/* Corner Fold */}
                      {formData.badgeStyle === 'corner-fold' && (
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 15,
                              right: -35,
                              bgcolor: formData.badgeColor,
                              color: formData.badgeTextColor,
                              px: 4,
                              py: 0.5,
                              fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              letterSpacing: '0.05em',
                              transform: 'rotate(45deg)',
                              transformOrigin: 'center',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                          >
                            {formData.badgeText || 'NYHET!'}
                          </Box>
                        </Box>
                      )}

                      {/* Circle */}
                      {formData.badgeStyle === 'circle' && (
                        <Box
                          sx={{
                            bgcolor: formData.badgeColor,
                            color: formData.badgeTextColor,
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            letterSpacing: '0.02em',
                            textAlign: 'center',
                            m: 1,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                          }}
                        >
                          {formData.badgeText || 'NYHET!'}
                        </Box>
                      )}

                      {/* Tag */}
                      {formData.badgeStyle === 'tag' && (
                        <Box
                          sx={{
                            bgcolor: formData.badgeColor,
                            color: formData.badgeTextColor,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '4px 0 0 4px',
                            fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            letterSpacing: '0.05em',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}
                        >
                          {formData.badgeText || 'NYHET!'}
                        </Box>
                      )}
                    </Box>

                    {/* Image */}
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: formData.bodyText?.trim() ? '16/10' : '16/12',
                        bgcolor: 'grey.200',
                        position: 'relative'
                      }}
                    >
                      {formData.imageUrl ? (
                        <img
                          src={getImageSrc(formData.imageUrl)}
                          alt="Preview"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <ImageIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                        </Box>
                      )}

                      {/* Title overlay - positioned based on titlePosition */}
                      <Box
                        sx={{
                          position: 'absolute',
                          ...(formData.titlePosition.includes('top') && { top: 0 }),
                          ...(formData.titlePosition.includes('middle') && { top: '50%', transform: 'translateY(-50%)' }),
                          ...(formData.titlePosition.includes('bottom') && { bottom: 0 }),
                          ...(formData.titlePosition.includes('left') && { left: 0, textAlign: 'left' }),
                          ...(formData.titlePosition.includes('center') && { left: 0, right: 0, textAlign: 'center' }),
                          ...(formData.titlePosition.includes('right') && { right: 0, textAlign: 'right' }),
                          ...(formData.titlePosition.includes('bottom') && {
                            background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                            left: 0,
                            right: 0,
                            pt: 4
                          }),
                          ...(formData.titlePosition.includes('top') && {
                            background: 'linear-gradient(rgba(0,0,0,0.7), transparent)',
                            left: 0,
                            right: 0,
                            pb: 4
                          }),
                          p: 2
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#ffffff',
                            fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                            fontWeight: 600,
                            fontSize: `${formData.titleSize * 0.52}rem`,
                            textShadow: '1px 1px 4px rgba(0,0,0,0.5)',
                            lineHeight: 1.1
                          }}
                        >
                          {formData.title || 'Rubrik...'}
                        </Typography>
                      </Box>

                      {/* CTA overlay when no body text */}
                      {!formData.bodyText?.trim() && formData.ctaText && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 16,
                            left: '50%',
                            transform: 'translateX(-50%)'
                          }}
                        >
                          <Button
                            variant="contained"
                            size="small"
                            sx={{
                              bgcolor: formData.ctaButtonColor,
                              fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                              '&:hover': { bgcolor: formData.ctaButtonColor, filter: 'brightness(1.1)' }
                            }}
                          >
                            {formData.ctaText} →
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Body section */}
                  {(formData.bodyText?.trim() || formData.bodyTitle?.trim()) && (
                    <Box sx={{ p: 2, bgcolor: formData.bodyBackgroundColor }}>
                      {/* Body Title */}
                      {formData.bodyTitle && (
                        <Typography
                          sx={{
                            color: formData.bodyTextColor,
                            fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                            fontWeight: 700,
                            fontSize: `${formData.bodyTitleSize}rem`,
                            mb: 1.5,
                            textTransform: 'uppercase',
                            letterSpacing: '0.02em'
                          }}
                        >
                          {formData.bodyTitle}
                        </Typography>
                      )}

                      {/* Body text with optional floating image */}
                      <Box>
                        {formData.bodyImagePosition !== 'none' && formData.bodyImageUrl && (
                          <Box
                            component="img"
                            src={getImageSrc(formData.bodyImageUrl)}
                            alt="Body image"
                            sx={{
                              width: '40%',
                              maxWidth: 150,
                              height: 'auto',
                              borderRadius: 1,
                              float: formData.bodyImagePosition,
                              ...(formData.bodyImagePosition === 'right' ? { ml: 2, mb: 1 } : { mr: 2, mb: 1 })
                            }}
                          />
                        )}
                        <Typography 
                          variant="body2" 
                          component="div"
                          sx={{ 
                            color: formData.bodyTextColor, 
                            lineHeight: 1.6,
                            '& a': { color: 'inherit', textDecoration: 'underline' },
                            '& b, & strong': { fontWeight: 700 },
                            '& i, & em': { fontStyle: 'italic' }
                          }}
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formData.bodyText || '') }}
                        />
                        <Box sx={{ clear: 'both' }} />
                      </Box>

                      {/* CTA - text link or button */}
                      {formData.ctaText && (
                        <Box sx={{ mt: 2 }}>
                          {formData.ctaStyle === 'text-link' ? (
                            <Typography
                              sx={{
                                color: formData.bodyTextColor,
                                fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.02em',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {formData.ctaText}
                            </Typography>
                          ) : (
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                bgcolor: formData.ctaButtonColor,
                                fontFamily: "'Kapra Neue', 'Barlow Condensed', sans-serif",
                                '&:hover': { bgcolor: formData.ctaButtonColor, filter: 'brightness(1.1)' }
                              }}
                            >
                              {formData.ctaText} →
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Paper>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Avbryt</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Sparar...' : 'Spara'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default PopupItemEditor;

